import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LeafletMap } from "@/components/LeafletMap";
import { FilterBar } from "@/components/FilterBar";
import { PropertyCard } from "@/components/PropertyCard";
import { School, Guitar as Hospital, Bus } from "lucide-react";

export const MapView = () => {
  const [props, setProps] = useState([]);
  const [neigh, setNeigh] = useState([]);
  const [filters, setFilters] = useState({});
  const [poisAll, setPoisAll] = useState({});
  const [toggles, setToggles] = useState({ schools: false, hospitals: false, transit: false });

  useEffect(() => {
    api.get("/neighborhoods").then(({ data }) => setNeigh(data));
    api.get("/pois").then(({ data }) => setPoisAll(data));
  }, []);

  useEffect(() => {
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    api.get("/properties", { params: { ...clean, limit: 100 } }).then(({ data }) => setProps(data));
  }, [filters]);

  const activePois = Object.fromEntries(
    Object.entries(poisAll).filter(([k]) => toggles[k])
  );

  const toggleKey = (k) => setToggles((t) => ({ ...t, [k]: !t[k] }));

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
      <div className="mb-6">
        <span className="section-eyebrow">Interactive</span>
        <h1 className="font-serif text-4xl text-[var(--teak)] mt-2">Mysuru Property Map</h1>
      </div>

      <div className="p-4 bg-white rounded-2xl border border-[var(--line)] mb-4 space-y-3">
        <FilterBar filters={filters} onChange={setFilters} compact />
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[var(--line)]" data-testid="poi-toggles">
          <span className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-semibold">Overlays:</span>
          <button
            onClick={() => toggleKey("schools")}
            data-testid="toggle-schools"
            className={`chip transition-colors ${toggles.schools ? "chip-emerald" : ""}`}
          >
            <School className="w-3.5 h-3.5" /> Schools ({poisAll.schools?.length || 0})
          </button>
          <button
            onClick={() => toggleKey("hospitals")}
            data-testid="toggle-hospitals"
            className={`chip transition-colors ${toggles.hospitals ? "chip-gold" : ""}`}
          >
            <Hospital className="w-3.5 h-3.5" /> Hospitals ({poisAll.hospitals?.length || 0})
          </button>
          <button
            onClick={() => toggleKey("transit")}
            data-testid="toggle-transit"
            className={`chip transition-colors ${toggles.transit ? "chip-gold" : ""}`}
          >
            <Bus className="w-3.5 h-3.5" /> Transit ({poisAll.transit?.length || 0})
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 h-[70vh]">
          <LeafletMap properties={props} neighborhoods={neigh} pois={activePois} height="100%" />
        </div>
        <div className="lg:col-span-2 h-[70vh] overflow-y-auto space-y-4 pr-2" data-testid="map-side-listings">
          {props.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          {props.length === 0 && (
            <div className="text-center text-[var(--ink-muted)] py-10">No matches</div>
          )}
        </div>
      </div>
    </div>
  );
};
